import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SUPPORTED_LOCALES, LOCALE_META, SupportedLocale } from '../../i18n/config';
import './MikaTranslationsModal.scss';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    /**
     * Title shown at the top of the modal. Typically "Përkthime: {name}" so
     * the admin sees which record they're editing.
     */
    title: string;
    /**
     * Initial value of each locale. Missing keys default to empty string.
     * Spatie returns this exact shape from the model accessor — the modal
     * normalizes it so all 7 locales are always rendered.
     */
    initialTranslations: Record<string, string>;
    /**
     * Called with the full 7-locale map when the admin clicks Save. Promise
     * support lets callers do a server round-trip and the modal stays in a
     * "saving..." state until it resolves.
     */
    onSave: (translations: Record<SupportedLocale, string>) => void | Promise<void>;
}

/**
 * Reusable modal for editing a translatable field across all 7 supported
 * locales. Renders one input per locale with the native-language name as
 * the label (e.g. "Shqip", "Deutsch", "Македонски") so admins recognise
 * each row at a glance regardless of their UI language.
 *
 * Behaviour:
 *   - At least one locale must be non-empty on save (otherwise the record
 *     would have no renderable name). Save button is disabled until that
 *     condition holds.
 *   - Auto-focuses the first empty input on open — speeds up the common
 *     "just add the English translation" flow.
 *   - Esc closes. Click outside closes. Both discard pending edits.
 *   - The active app locale gets a subtle "this is your language" pill so
 *     the admin recognises their primary editing target.
 */
const MikaTranslationsModal: React.FC<Props> = ({
    isOpen,
    onClose,
    title,
    initialTranslations,
    onSave,
}) => {
    const intl = useIntl();
    const activeLocale = intl.locale as SupportedLocale;
    const [values, setValues] = useState<Record<SupportedLocale, string>>(() =>
        normaliseInput(initialTranslations),
    );
    const [saving, setSaving] = useState(false);

    // Re-initialise when the modal opens or the source data changes — admin
    // might open the modal on Category A, close, open on Category B; we have
    // to reset the form to B's values.
    useEffect(() => {
        if (isOpen) {
            setValues(normaliseInput(initialTranslations));
            setSaving(false);
        }
    }, [isOpen, initialTranslations]);

    // Esc closes — don't bind when closed so we don't fight other dialogs.
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const hasAnyValue = Object.values(values).some((v) => v.trim() !== '');

    const handleSave = async () => {
        if (!hasAnyValue || saving) return;
        setSaving(true);
        try {
            await onSave(values);
        } finally {
            // The callsite typically closes the modal on success; if it
            // doesn't (e.g. server error toast and stay open), the saving
            // flag flips back so the button is interactive again.
            setSaving(false);
        }
    };

    return (
        <div className="mika-translations-overlay" onClick={onClose}>
            <div
                className="mika-translations-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="mika-translations-title"
            >
                <header className="mika-translations-head">
                    <h3 className="mika-translations-title" id="mika-translations-title">
                        {title}
                    </h3>
                    <button
                        type="button"
                        className="mika-translations-close"
                        onClick={onClose}
                        aria-label={intl.formatMessage({ id: 'common.confirm.cancel', defaultMessage: 'Cancel' })}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="6" y1="6" x2="18" y2="18" />
                            <line x1="18" y1="6" x2="6" y2="18" />
                        </svg>
                    </button>
                </header>

                <p className="mika-translations-desc">
                    <FormattedMessage
                        id="common.translations.description"
                        defaultMessage="Fill in each language. Empty fields fall back to the default language."
                    />
                </p>

                <div className="mika-translations-body">
                    {SUPPORTED_LOCALES.map((locale) => {
                        const meta = LOCALE_META[locale];
                        const isActive = locale === activeLocale;
                        return (
                            <div
                                key={locale}
                                className={`mika-translations-row ${isActive ? 'is-active-locale' : ''}`}
                            >
                                <label
                                    htmlFor={`mika-translations-input-${locale}`}
                                    className="mika-translations-row-label"
                                >
                                    <span className="mika-translations-row-native">{meta.native}</span>
                                    <span className="mika-translations-row-code">{locale.toUpperCase()}</span>
                                    {isActive && (
                                        <span className="mika-translations-row-current">
                                            <FormattedMessage
                                                id="common.translations.currentBadge"
                                                defaultMessage="Your language"
                                            />
                                        </span>
                                    )}
                                </label>
                                <input
                                    id={`mika-translations-input-${locale}`}
                                    type="text"
                                    className="mika-translations-row-input"
                                    value={values[locale] ?? ''}
                                    onChange={(e) =>
                                        setValues((prev) => ({ ...prev, [locale]: e.target.value }))
                                    }
                                    maxLength={255}
                                    placeholder={meta.english}
                                    autoFocus={isActive && !values[locale]}
                                />
                            </div>
                        );
                    })}
                </div>

                <footer className="mika-translations-foot">
                    <button
                        type="button"
                        className="mika-translations-btn mika-translations-btn--cancel"
                        onClick={onClose}
                        disabled={saving}
                    >
                        <FormattedMessage id="common.confirm.cancel" defaultMessage="Cancel" />
                    </button>
                    <button
                        type="button"
                        className="mika-translations-btn mika-translations-btn--save"
                        onClick={handleSave}
                        disabled={!hasAnyValue || saving}
                    >
                        {saving ? (
                            <FormattedMessage id="common.translations.saving" defaultMessage="Saving…" />
                        ) : (
                            <FormattedMessage id="common.translations.save" defaultMessage="Save translations" />
                        )}
                    </button>
                </footer>
            </div>
        </div>
    );
};

/**
 * Normalise the input map so every supported locale has a string entry
 * (empty if missing). Saves the render loop from `values[locale] ?? ''`
 * everywhere.
 */
function normaliseInput(input: Record<string, string>): Record<SupportedLocale, string> {
    const result = {} as Record<SupportedLocale, string>;
    for (const locale of SUPPORTED_LOCALES) {
        result[locale] = typeof input[locale] === 'string' ? input[locale] : '';
    }
    return result;
}

export default MikaTranslationsModal;
